import { config, isReproNode } from '../../../pedigree-tool/pedigree/config';

// Which PERSON a legend drop at a screen point lands on.
//
// This used to be three copies of a nearest-centre search over `.node` elements
// — the clip-path shapes, which are never painted — followed by a pid lookup
// that fell back to an INDEX into a different list (`.te`, which also holds
// unions), or, for diseases, to whoever the previous drop had picked. A drop that
// did not resolve through the pid could therefore write to another person, a
// union (a 400 with a generic error) or a stale target.
//
// Now: hit-test the painted symbols (`path.te`, the ones the user can see), keep
// only people, map a proxy to the person it redraws, and find the live record by
// its stable identity. There is no index fallback and no "previous target"
// fallback — a drop that does not resolve to one person writes nothing and says
// why.

/** CSS px from a symbol's centre that still counts as "on" it. */
export const DROP_RADIUS = 50;

export const DROP_REFUSAL = {
  outOfRange: 'out-of-range',
  notAPerson: 'not-a-person',
  notFound: 'not-found',
  unsaved: 'unsaved'
};

/** The chart this legend belongs to — scoped, so a hidden workspace tab is never hit. */
export const chartSvgIn = (root) =>
  (root || document).querySelector('svg.pedigree-chart-svg');

const datumOf = (el) => {
  const d = el?.__data__;
  return d?.data && d.pid == null ? d.data : d;
};

/**
 * The painted symbol under — or nearest to — a screen point.
 *
 * @param {Element|null} root  the instance container (null = document)
 * @param {{x: number, y: number}} point  client coordinates
 * @returns {{ datum: object, dist: number } | null}
 */
export const findDropCandidate = (root, point, radius = DROP_RADIUS) => {
  const svg = chartSvgIn(root);
  if (!svg || point?.x == null || point?.y == null) return null;

  let best = null;
  svg.querySelectorAll('g[data-selection-pid] > path.te').forEach((el) => {
    const datum = datumOf(el);
    // Unions are drawn as a zero-area .te; hidden nodes are display:none.
    if (!datum || datum.kind === config.NODE_TYPE.UNION || datum.hidden) {
      return;
    }
    const r = el.getBoundingClientRect();
    if (!r.width && !r.height) return;

    const inside =
      point.x >= r.left &&
      point.x <= r.right &&
      point.y >= r.top &&
      point.y <= r.bottom;
    const dist = Math.hypot(
      point.x - (r.left + r.width / 2),
      point.y - (r.top + r.height / 2)
    );
    if (!inside && dist > radius) return;
    if (!best || dist < best.dist) best = { datum, dist };
  });
  return best;
};

const sameId = (a, b) => a != null && b != null && String(a) === String(b);

/**
 * Map a symbol's datum to the live member record it stands for.
 *
 * Matched on `_id` first, then `uid`, and only then on pid. pids are reused
 * within a family (a new relative can take a deleted one's number) and a proxy or
 * fetus node is numbered max(pid)+1, so pid alone can name the wrong row. A
 * proxy is a clone of the person it redraws: it keeps that person's `_id`, and
 * `proxyOf` names their pid.
 *
 * @returns {{ member: object, canvasPid: * } | { reason: string, canvasPid?: * }}
 */
export const resolveDropMember = (datum, memberRecords) => {
  if (!datum) return { reason: DROP_REFUSAL.outOfRange };
  const canvasPid = datum.pid;
  if (isReproNode(datum) || datum.deleted) {
    return { reason: DROP_REFUSAL.notAPerson, canvasPid };
  }

  const records = Array.isArray(memberRecords) ? memberRecords : [];
  const ownerPid = datum.isProxy ? datum.proxyOf : datum.pid;
  const member =
    (datum._id && records.find((m) => sameId(m._id, datum._id))) ||
    (!datum.isProxy &&
      datum.uid &&
      records.find((m) => sameId(m.uid, datum.uid))) ||
    records.find((m) => sameId(m.pid, ownerPid));

  if (!member) return { reason: DROP_REFUSAL.notFound, canvasPid };
  if (member.deleted) return { reason: DROP_REFUSAL.notAPerson, canvasPid };
  // No identifier yet = never saved. The record routes address a person by it,
  // so there is nowhere to write until the pedigree has been saved.
  if (!(member.proBandId ?? member.familyMemberId)) {
    return { reason: DROP_REFUSAL.unsaved, canvasPid };
  }
  return { member, canvasPid };
};

/** What to tell the user when a drop is refused. */
export const refusalMessage = (reason, itemName) => {
  switch (reason) {
    case DROP_REFUSAL.outOfRange:
      return `Drop ${itemName} onto a person's symbol to add it to their record.`;
    case DROP_REFUSAL.notAPerson:
      return 'Diseases, phenotypes and genotypes can only be added to a person.';
    case DROP_REFUSAL.unsaved:
      return 'Please save the pedigree first';
    default:
      return 'That person could not be found. Please reload the pedigree and try again.';
  }
};
